import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../assets/logolast.png';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isToursDropdownOpen, setIsToursDropdownOpen] = useState(false);

  // Toggle the mobile menu
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  // Show and hide the dropdown menu
  const handleMouseEnter = () => setIsToursDropdownOpen(true);
  const handleMouseLeave = () => setIsToursDropdownOpen(false);
  const handleClick = () => setIsToursDropdownOpen((prev) => !prev);

  // Animation variants
  const navVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const linkVariants = {
    hover: { scale: 1.1, color: '#22c55e', transition: { duration: 0.3 } },
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.2, ease: 'easeIn' },
    },
  };

  const mobileMenuVariants = {
    hidden: { opacity: 0, x: '100%' },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      x: '100%',
      transition: { duration: 0.3, ease: 'easeIn' },
    },
  };

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isToursDropdownOpen) {
        setIsToursDropdownOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isToursDropdownOpen]);

  const tours = [
    { to: '/waterfall', label: 'La Paz Waterfall' },
    { to: '/poasmirador', label: 'Poas Volcano and Mirador Cinchona tour' },
    { to: '/sarchi', label: 'Sarchí and Grecia Tour' },
    { to: '/poasdoka', label: 'Poás Volcano & Doka Coffee' },
    { to: '/poasvolcano', label: 'Tour of Poás Volcano' },

    { to: '/doka', label: 'Doka Coffee Plantation' },
  ];

  return (
    <motion.header
      className='sticky top-0 z-50 w-full bg-gradient-to-r from-black to-gray-900 shadow-lg'
      initial='hidden'
      animate='visible'
      variants={navVariants}
    >
      <nav className='flex items-center justify-between px-6 md:px-16 lg:px-32 xl:px-64 py-4'>
        {/* Logo */}
        <Link to='/'>
          <motion.img
            src={logo}
            alt='EcoTours Logo'
            className='h-16 md:h-20 lg:h-24'
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ duration: 0.3 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        </Link>

        {/* Desktop Navigation */}
        <ul className='hidden md:flex list-none gap-8 lg:gap-12 items-center'>
          <motion.li whileHover='hover' variants={linkVariants}>
            <Link
              to='/'
              className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Home
            </Link>
          </motion.li>
          <motion.li whileHover='hover' variants={linkVariants}>
            <Link
              to='/birding'
              className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Birding Tours
            </Link>
          </motion.li>
          <motion.li whileHover='hover' variants={linkVariants}>
            <div
              className='relative'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to='/services'
                className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 cursor-pointer'
                onClick={handleClick}
                aria-expanded={isToursDropdownOpen}
                aria-haspopup="true"
              >
                Tours
              </Link>
              {isToursDropdownOpen && (
                <motion.ul
                  role="menu"
                  className='absolute top-full left-0 bg-gray-800 text-gray-50 mt-0 p-2 shadow-lg  rounded-md w-48'
                  initial='hidden'
                  animate='visible'
                  exit='exit'
                  variants={dropdownVariants}
                >
                  {tours.map((tour, index) => (
                    <motion.li
                      key={index}
                      role="menuitem"
                      whileHover={{ backgroundColor: '#374151' }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={tour.to}
                        className='block px-4 py-2 text-sm font-medium text-gray-300 hover:text-green-500'
                        onClick={() => {
                          setIsToursDropdownOpen(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {tour.label}
                      </Link>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </div>
          </motion.li>
          <motion.li whileHover='hover' variants={linkVariants}>
            <Link
              to='/wildlife'
              className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Wildlife
            </Link>
          </motion.li>
          <motion.li whileHover='hover' variants={linkVariants}>
            <Link
              to='/transport'
              className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Transport
            </Link>
          </motion.li>
          <motion.li whileHover='hover' variants={linkVariants}>
            <Link
              to='/guide'
              className='font-semibold text-gray-50 hover:text-green-500 transition-colors duration-200 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Meet Michael
            </Link>
          </motion.li>
          <motion.li whileHover={{ scale: 1.05 }}>
            <Link
              to='/contact'
              className='font-semibold text-gray-50 border-2 border-green-600 px-6 py-2 rounded-full hover:bg-green-600 hover:text-white transition-all duration-300 whitespace-nowrap'
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Plan Your Trip
            </Link>
          </motion.li>
        </ul>

        {/* Mobile Hamburger Menu */}
        <div className='md:hidden m-2 px-4'>
          <motion.button
            className='text-gray-50 focus:outline-none'
            onClick={toggleMenu}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            role="button"
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            {isMenuOpen ? (
              <span className='text-3xl font-bold'>×</span>
            ) : (
              <div
                className='w-12 h-12 right-10 flex items-center justify-items-start text-2xl'
                aria-label='Menu'
              >
                ☰
              </div>
            )}
          </motion.button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          className='fixed top-24 inset-0 bg-gradient-to-b from-gray-900 to-black z-50 flex flex-col items-start p-6'
          initial='hidden'
          animate='visible'
          exit='exit'
          variants={mobileMenuVariants}
        >
          <ul className='list-none flex flex-col gap-6 mt-12 w-full'>
            {[
              { to: '/', label: 'Home' },
              { to: '/birding', label: 'Birding Tours' },
            ].map((item, index) => (
              <motion.li
                key={index}
                whileHover={{ x: 10, color: '#22c55e' }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={item.to}
                  className='text-lg text-gray-50 font-bold hover:text-green-500'
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {item.label}
                </Link>
                <hr className='w-full border-gray-400 mt-2' />
              </motion.li>
            ))}
            <motion.li
              whileHover={{ x: 10, color: '#22c55e' }}
              transition={{ duration: 0.2 }}
            >
              <span
                className='text-lg text-gray-50 font-bold hover:text-green-500 cursor-pointer'
                onClick={handleClick}
              >
                Tours
              </span>
              {isToursDropdownOpen && (
                <motion.ul
                  className='ml-4 mt-2 flex flex-col gap-2'
                  initial='hidden'
                  animate='visible'
                  variants={dropdownVariants}
                >
                  {tours.map((tour, index) => (
                    <motion.li
                      key={index}
                      whileHover={{ x: 5, color: '#22c55e' }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={tour.to}
                        className='text-lg text-gray-300 font-medium hover:text-green-500'
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsToursDropdownOpen(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {tour.label}
                      </Link>
                    </motion.li>
                  ))}
                </motion.ul>
              )}
              <hr className='w-full border-gray-400 mt-2' />
            </motion.li>
            {[
              { to: '/wildlife', label: 'Wildlife' },
              { to: '/transport', label: 'Transport' },
              { to: '/guide', label: 'Meet Michael' },
            ].map((item, index) => (
              <motion.li
                key={index}
                whileHover={{ x: 10, color: '#22c55e' }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={item.to}
                  className='text-lg text-gray-50 font-bold hover:text-green-500'
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  {item.label}
                </Link>
                <hr className='w-full border-gray-400 mt-2' />
              </motion.li>
            ))}
            <motion.li whileHover={{ scale: 1.05 }} className='w-full'>
              <Link
                to='/contact'
                className='block text-center text-lg font-medium text-gray-50 border-2 border-gray-300 rounded-md py-3 hover:bg-gray-700 hover:text-gray-50 transition-all duration-300'
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Plan Your Trip
              </Link>
            </motion.li>
          </ul>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Navbar;
